<template>
  <ui-modal v-model="model">
    <div class="p-2 space-y-4">
      <h3 v-text="t('reportbug.heading')"/>
      <p class="text-gray-500 font-medium leading-7" v-text="t('reportbug.description')"/>
      <p class="text-gray-500 font-medium leading-7 space-y-2 py-4">
        <ui-link :href="'mailto:'+appMailSupport" iconleft :text="appMailSupport"/>
        <br>
        <ui-link href="tel:+43723523385" text="+43 7235 233 85" iconleft/>
      </p>
      <div>
        <textarea
          v-model="dataBugText"
          class=""
          :placeholder="t('reportbug.placeholder')"
          rows="5"
          />
        <div v-if="isAuthed">
          <div class="underline text-gray-500 cursor-pointer py-2" @click.self="refFileModal.open()">
            {{ t('reportbug.addfile') }}
            <ui-file-modal
              ref="refFileModal"
              v-model:uploading="dataIsUploading"
              :types="[
                'image',
                'text/plain'
              ]"
              @file="methodAddedFile"
              />
          </div>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="(file,i) in dataFilesList"
              :key="'file_'+i"
              class="min-w-[140px] w-[30%] flex-grow overflow-hidden bg-gray-200 rounded p-1 truncate relative"
              @click="methodOpenUrl(file.url)"
              >
              <div class="truncate pr-6" v-text="file?.name ?? '[no name]'" />
              <div class="text-xs text-gray-500" v-text="'Size: '+(file?.size ?? '[no size]')"/>
              <div class="absolute right-2 top-2">
                <i class="fas fa-trash text-red-700 cursor-pointer" @click.stop="dataFilesList.splice(i,1)"/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ui-button
        v-if="!dataIsSending"
        class="dark w-full"
        :text="t('Send')"
        @click="methodSubmitBug"
        />
      <div
        v-else
        class="w-full p-2 bg-gray-300 animate-pulse text-center"
        v-text="'Sending ...'"
        />
    </div>
  </ui-modal>
</template>

<script setup lang="ts">
const model = defineModel<boolean>()
const dataBugText = ref<string>('')
const dataIsSending = ref<boolean>(false)
const dataFilesList = ref<any[]>([])
const dataIsUploading = ref<boolean>(false)
const refFileModal = ref<any>()

const { t } = useI18n({
  messages: {
    en: {
      reportbug: {
        heading: 'Report a bug',
        description: 'If you have experienced a bug, an inconvenience or something that is not working as expected, please let us know.',
        placeholder: 'It happened ...',
        addfile: 'Add a file',
      },
    },
    de: {
      reportbug: {
        heading: 'Fehler melden',
        description: 'Wenn Sie einen Fehler, eine Unannehmlichkeit oder etwas bemerkt haben, das nicht wie erwartet funktioniert, lassen Sie es uns bitte wissen.',
        placeholder: 'Es ist passiert ...',
        addfile: 'Datei hinzufügen',
      },
    },
    uk: {
      reportbug: {
        heading: 'Повідомити про помилку',
        description: 'Якщо ви зіткнулися з помилкою, незручністю або чимось, що не працює як очікувалося, будь ласка, дайте нам знати.',
        placeholder: 'Це сталося ...',
        addfile: 'Додати файл',
      },
    },
    es: {
      reportbug: {
        heading: 'Reportar un error',
        description: 'Si ha experimentado un error, una molestia o algo que no funciona como se esperaba, háganoslo saber.',
        placeholder: 'Ocurrió ...',
        addfile: 'Agregar un archivo',
      },
    }
  } as any
})

const methodSubmitBug = () => {

  if(dataIsUploading.value){
    return notify({
      text: 'Please wait until all files are uploaded',
      type: 'warning'
    })
  }

  if(dataBugText.value.length <= 1){
    model.value = false;
    return notify('No bug? Sounds great!')
  }

  dataIsSending.value = true

  const payload = {
    text: dataBugText.value,
    files: dataFilesList.value.map((file) => (file?.file ?? file).id)
  }

  const doFinally = () => {
    dataIsSending.value = false
  }

  if(ws.is_setup.value){
    ws.send('app.reportbug', payload)
      .then((response) => {
        notify(response?.message ?? response)
        model.value = false
        dataBugText.value = ''
        dataFilesList.value = []
      })
      .catch(api.parseError)
      .finally(doFinally)
  }else{
    api.post('reportbug',payload)
      .then((response: any) => {
        notify(response?.data?.message ?? response?.data)
        model.value = false
        dataBugText.value = ''
        dataFilesList.value = []
      })
      .catch(api.parseError)
      .finally(doFinally)
  }
}

const methodAddedFile = (file: any) => {
  console.log('file added', file)
  dataFilesList.value.push(file?.file ?? file)
}

const methodOpenUrl = (url: string) => {
  window.open(url)
}

watch(model, () => {
  dataIsSending.value = false;
})
</script>
